import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Mail, CheckCircle } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { apiFetch } from '../lib/apiClient';
import TurnstileWidget from './TurnstileWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const NICOLA_HERO_IMAGE = '/media/nicola-hero.webp';

const NewsSection = () => {
  const [articles, setArticles] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [articlesRef, articlesVisible] = useScrollAnimation();
  const [sidebarRef, sidebarVisible] = useScrollAnimation();
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [website, setWebsite] = useState('');
  const [newsletterConsent, setNewsletterConsent] = useState(false);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const cacheBuster = `_t=${Date.now()}`;
        const response = await apiFetch(`${API_URL}/api/news?${cacheBuster}`, {
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        });
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setArticles(data);
          }
        }
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchFeatured = async () => {
      try {
        const cacheBuster = `_t=${Date.now()}`;
        const response = await apiFetch(`${API_URL}/api/news/featured/list?${cacheBuster}`, {
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        });
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setFeaturedArticles(data);
          }
        }
      } catch (error) {
        console.error('Error fetching featured:', error);
      }
    };

    fetchNews();
    fetchFeatured();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || !turnstileToken || !newsletterConsent) return;
    
    setSubscribing(true);
    try {
      const response = await apiFetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), website, turnstileToken, consent: newsletterConsent })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.detail || 'Iscrizione non riuscita');
      }
      
      if (data.already_subscribed) {
        setSubscribeStatus({ type: 'info', message: 'Email già iscritta!' });
      } else {
        setSubscribeStatus({ type: 'success', message: 'Iscrizione completata!' });
        setEmail('');
      }
    } catch (error) {
      setSubscribeStatus({ type: 'error', message: 'Errore, riprova più tardi' });
    } finally {
      setSubscribing(false);
      setTimeout(() => setSubscribeStatus(null), 4000);
    }
  };

  // An empty article archive must not remove the independent newsletter form.
  const hasArticles = isLoading || articles.length > 0;

  return (
    <section id="news" className="bg-[#f1f5f9] py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left - Articles */}
          {hasArticles && <div
            ref={articlesRef}
            className={`lg:col-span-7 animate-fade-left ${articlesVisible ? 'animated' : ''}`}
          >
            <p className="text-gray-400 tracking-[0.3em] text-sm font-medium mb-12">
              Insights & strategie
            </p>
            
            <div className="space-y-0">
              {articles.slice(0, 3).map((article, index) => (
                <div key={article.id || index}>
                  {/* Article Row */}
                  <Link 
                    to={article.id ? `/insight/${article.id}` : '/insight'}
                    className="flex gap-6 lg:gap-10 py-10 group cursor-pointer block"
                  >
                    {/* Left bar */}
                    <div className="w-1 bg-[#0b114e] flex-shrink-0 self-stretch group-hover:bg-[#d4883a] transition-colors" />
                    
                    {/* Content */}
                    <div className="flex-1 flex flex-col lg:flex-row gap-6 lg:gap-10">
                      {/* Text */}
                      <div className="flex-1">
                        {/* Category & Read time */}
                        <div className="flex items-center gap-4 mb-3">
                          <span className="text-[#d4883a] text-sm font-medium">{article.category}</span>
                          <span className="flex items-center gap-1 text-gray-400 text-sm">
                            <Clock className="w-4 h-4" />
                            {article.read_time || article.readTime || '5 min'}
                          </span>
                        </div>
                        
                        <h3 
                          className="text-xl lg:text-2xl font-bold text-[#0b114e] leading-tight group-hover:text-[#d4883a] transition-colors mb-3"
                          style={{ fontFamily: 'Georgia, serif' }}
                        >
                          {article.title}
                        </h3>
                        <p className="text-gray-500 leading-relaxed line-clamp-3">
                          {article.excerpt}
                        </p>
                      </div>
                      
                      {/* Image */}
                      <div className="flex-shrink-0">
                        <img
                          src={article.imageUrl || article.image || article.image_url || NICOLA_HERO_IMAGE}
                          alt={article.title}
                          className="w-full lg:w-48 h-32 lg:h-36 object-cover rounded-xl grayscale group-hover:grayscale-0 transition-[filter] duration-500"
                          style={{ objectPosition: '25% top', willChange: 'filter' }}
                          onError={(e) => { e.target.src = NICOLA_HERO_IMAGE; }}
                        />
                      </div>
                    </div>
                  </Link>
                  
                  {/* Divider */}
                  {index < Math.min(articles.length, 3) - 1 && (
                    <div className="border-b border-gray-200" />
                  )}
                </div>
              ))}
            </div>

            {/* View All */}
            <div className="mt-10">
              <Link 
                to="/insight" 
                className="inline-flex items-center gap-2 text-[#0b114e] font-semibold hover:text-[#d4883a] transition-colors group"
              >
                Tutti gli articoli
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>}

          {/* Vertical Divider Line */}
          {hasArticles && <div className="hidden lg:flex lg:col-span-1 justify-center">
            <div className="w-px bg-gray-300 h-full" />
          </div>}

          {/* Right - Quick Insights */}
          <div 
            ref={sidebarRef}
            className={`${hasArticles ? 'lg:col-span-4' : 'lg:col-span-6 lg:col-start-4'} animate-fade-right ${sidebarVisible ? 'animated' : ''}`}
          >
            <div className="lg:sticky lg:top-32">
              {featuredArticles.length > 0 && <h3
                className="text-2xl lg:text-3xl text-[#0b114e] mb-8 leading-tight"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Letture<br />consigliate:
              </h3>}
              
              <ul className="space-y-5">
                {featuredArticles.slice(0, 4).map((article, index) => (
                  <li key={article.id}>
                    <Link 
                      to={`/insight/${article.id}`}
                      className="flex items-start gap-3 group"
                    >
                      <span className="w-6 h-6 bg-[#d4883a]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#d4883a]/20 transition-colors">
                        <span className="text-[#d4883a] text-sm font-bold">{index + 1}</span>
                      </span>
                      <span 
                        className="text-gray-600 group-hover:text-[#0b114e] transition-colors leading-relaxed"
                      >
                        {article.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Newsletter CTA */}
              <div className="mt-10 p-6 bg-[#0b114e] rounded-2xl">
                <h4 className="text-white font-bold mb-2">Newsletter Esclusiva</h4>
                <p className="text-white/60 text-sm mb-4">
                  Strategie riservate ai decision maker. Una email a settimana.
                </p>
                
                {subscribeStatus ? (
                  <div className={`flex items-center gap-2 py-3 px-4 rounded-xl text-sm ${
                    subscribeStatus.type === 'success' ? 'bg-green-500/20 text-green-300' :
                    subscribeStatus.type === 'error' ? 'bg-red-500/20 text-red-300' :
                    'bg-blue-500/20 text-blue-300'
                  }`}>
                    <CheckCircle className="w-4 h-4" />
                    {subscribeStatus.message}
                  </div>
                ) : (
                  <form onSubmit={handleSubscribe} className="space-y-3">
                    <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="La tua email"
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#d4883a] transition-colors"
                      style={{ fontSize: '16px' }}
                    />
                    <label className="flex items-start gap-2 text-xs text-white/70">
                      <input type="checkbox" required checked={newsletterConsent}
                        onChange={(event) => setNewsletterConsent(event.target.checked)} className="mt-0.5" />
                      <span>Acconsento a ricevere la newsletter e ho letto la <Link to="/privacy-policy" className="underline">Privacy Policy</Link>.</span>
                    </label>
                    <TurnstileWidget action="newsletter" onVerify={setTurnstileToken} />
                    <button 
                      type="submit"
                      disabled={subscribing || !turnstileToken || !newsletterConsent}
                      className="w-full bg-[#d4883a] hover:bg-[#d4896a] disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
                    >
                      {subscribing ? (
                        'Iscrizione...'
                      ) : (
                        <>
                          <Mail className="w-4 h-4" />
                          Iscriviti Ora
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
